import { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useEditionAwareness } from '../services/edition-awareness-context';
import { useAnalytics } from './use-analytics';

/**
 * One off event which tracks when a customer is eligible/ineligible for edition awareness.
 */
function useTrackAudienceAndTiming({
	isEligible,
	shouldTrack,
}: {
	isEligible: boolean;
	shouldTrack?: boolean;
}) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (!shouldTrack || hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'audienceAndTiming',
			actionSubjectId: 'editionAwareness',
			action: 'checked',
			attributes: {
				isEligible,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, isEligible, shouldTrack]);
}

/**
 * Sends an ineligible audience and timing event for free, in-trial, or in-predunning customers,
 * as well as for premium site admins who are not currently in a trial or predunning.
 */
export function IneligibleAnalytics(): null {
	const { edition, isInTrial, isInPredunning, userRoles } = useEditionAwareness();

	const isPremiumSiteAdminNotInTrialOrPredunning = Boolean(
		fg('analytic_for_premium_site_admin') &&
		edition === 'premium' &&
		userRoles?.includes('SITE_ADMIN') &&
		!isInTrial &&
		!isInPredunning,
	);

	const shouldTrack = Boolean(
		edition === 'free' || isInTrial || isInPredunning || isPremiumSiteAdminNotInTrialOrPredunning,
	);

	useTrackAudienceAndTiming({ isEligible: false, shouldTrack });

	return null;
}
